import { t } from "@hakili/i18n";
import { Modal, Verdict } from "@hakili/ui";
import { AlertTriangle, Minimize2 } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";

import { fill } from "./dossiers.js";

/**
 * `ARC-1`, WF-BNK-06, D2 frame 26: **the pièce viewer, over the real pièce.**
 *
 * D2 draws a stylised page; the build shows the file the client deposited, by the door that was made for it
 * (`GET /api/v1/bank/files/{id}/pieces/{code}/file`, right `consulterPieceDossierBanque`, one audit line per opening):
 * one signed link per file, in upload order, 900 seconds. Three laws, from the AA-AD architect:
 *
 * 1. **The demonstration door's image (`/pieces/{pieceId}/image`) is never shown for a real dossier.** This component has
 *    no other source than the door above; nothing in the app fetches the demonstration image any more.
 * 2. **The door is asked on EVERY opening.** The viewer mounts once per opening (the caller keys it) and asks in its own
 *    effect; no link is kept anywhere, because a link lives 900 seconds and a kept one would be a dead one.
 * 3. **A 404 is a pièce that is not available, never an error page.** The refusing cases (another bank, a refus, a
 *    withdrawn file, an unknown or unreceived pièce, no file) are one answer by design, and the screen says one thing.
 *
 * « Vérifications de Hakili » (frame 26) is not drawn: no door carries a pièce's checks (the server half goes to the AA to AD architect).
 * « déposé le {date} » is the caller's (the point's `source` line), from the evidence's stored receipt time in the shared `formatDate`.
 */
export interface DocumentFileView {
  readonly fileName: string;
  readonly mimeType: string;
  readonly url: string;
}

/** The door's answer, strictly; `null` when it is not the shape the contract promises (the viewer then says « indisponible »). */
export function documentFilesFrom(answer: unknown): readonly DocumentFileView[] | null {
  if (typeof answer !== "object" || answer === null) return null;
  const files = (answer as Record<string, unknown>)["files"];
  if (!Array.isArray(files)) return null;
  const out: DocumentFileView[] = [];
  for (const one of files) {
    if (typeof one !== "object" || one === null) return null;
    const row = one as Record<string, unknown>;
    const fileName = row["fileName"];
    const mimeType = row["mimeType"];
    const url = row["url"];
    if (typeof fileName !== "string" || typeof mimeType !== "string" || typeof url !== "string") return null;
    out.push({ fileName, mimeType, url });
  }
  return out;
}

/** What the viewer asks: the files in upload order, or `null` for a pièce the door does not open (the 404, one answer). */
export type DocumentDoor = () => Promise<readonly DocumentFileView[] | null>;

type Phase =
  { readonly kind: "loading" } | { readonly kind: "files"; readonly files: readonly DocumentFileView[] };

const NOT_AVAILABLE = { kind: "files", files: [] } as const satisfies Phase;

function DocumentFile(props: {
  readonly file: DocumentFileView;
  readonly index: number;
  readonly total: number;
}): ReactElement {
  const { file } = props;
  const caption = fill("bank.visionneuse.page", { n: String(props.index + 1), total: String(props.total) });
  return (
    <figure className="m-0" data-testid={`bank-visionneuse-fichier-${String(props.index)}`}>
      <div className="grid min-h-[260px] content-start overflow-hidden rounded-lg bg-white p-[18px] shadow-[0_1px_2px_rgba(14,23,48,.12)]">
        {file.mimeType === "application/pdf" ? (
          <object
            data={file.url}
            type="application/pdf"
            aria-label={file.fileName}
            className="h-[420px] w-full"
            data-testid={`bank-visionneuse-pdf-${String(props.index)}`}
          >
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[color:var(--hk-brand-strong)]"
            >
              {file.fileName}
            </a>
          </object>
        ) : (
          <img
            src={file.url}
            alt={file.fileName}
            className="max-h-[420px] w-full object-contain"
            data-testid={`bank-visionneuse-image-${String(props.index)}`}
          />
        )}
      </div>
      <figcaption
        className="mt-1.5 text-center text-sm text-[color:var(--hk-text-2)]"
        data-testid={`bank-visionneuse-page-${String(props.index)}`}
      >
        {caption}
      </figcaption>
    </figure>
  );
}

export interface DocumentCheck {
  readonly kind: "conforme" | "non_conforme" | "incertain" | "signale" | "none";
  readonly label: string;
  readonly reason: string;
}

export function DocumentViewer(props: {
  /** The point's own label, D2's title. */
  readonly title: string;
  /** « page 2 », from the evidence's own page, or `null` where the point has none. */
  readonly source: string | null;
  readonly door: DocumentDoor;
  /** `null` where the pièce is opened from the conversation's strip, with no point behind it: no extract, no conclusion. */
  readonly verdict: {
    readonly kind: "conforme" | "non_conforme" | "incertain" | "none";
    readonly label: string;
  } | null;
  /**
   * `ARC1-CHECKS` (D2 frame 26): what Hakili STORED about this pièce, one line each, in its own sentences: the points that read it,
   * then the register's findings tied to it. Empty, the block (heading included) is not drawn.
   */
  readonly checks: readonly DocumentCheck[];
  /** Hakili's own reason for the point: the excerpt and the conclusion. */
  readonly reason: string | null;
  /** « Signaler » and « Marquer conforme » stand only while the point is open on the analyst's own review; else « Consultation ». */
  readonly acts: { readonly onFlag: () => void; readonly onConform: () => void } | null;
  /**
   * `ASK-ONE-PATTERN` (frames 179, 180): the viewer opened FROM THE CONVERSATION says where it came from, offers « Consultation seule. »
   * and one button, « Retour à la conversation ». Absent, the viewer is the review table's and keeps its words.
   */
  readonly fromConversation?: boolean;
  readonly onClose: () => void;
}): ReactElement {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const close = useRef<HTMLButtonElement | null>(null);
  // Asked once per opening: the caller keys the viewer, and nothing here depends on the door's identity.
  const doorRef = useRef(props.door);
  const closeRef = useRef(props.onClose);
  useEffect(() => {
    let live = true;
    doorRef.current().then(
      (files) => {
        if (live) setPhase(files === null ? NOT_AVAILABLE : { kind: "files", files });
      },
      () => {
        if (live) setPhase(NOT_AVAILABLE);
      },
    );
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    close.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  const files = phase.kind === "files" ? phase.files : [];
  return (
    <Modal
      open
      title={props.title}
      onClose={props.onClose}
      closeLabel={t("bank.visionneuse.fermer")}
      size="wide"
      cross={false}
    >
      <div data-testid="bank-visionneuse">
        {props.fromConversation === true ? (
          <p className="-mt-3 text-sm text-[color:var(--hk-text-2)]" data-testid="bank-visionneuse-source">
            {t("bank.conversation.pieceOrigine")}
          </p>
        ) : props.source === null ? null : (
          <p className="-mt-3 text-sm text-[color:var(--hk-text-2)]" data-testid="bank-visionneuse-source">
            {props.source}
          </p>
        )}
        {phase.kind === "loading" || files.length === 0 ? (
          // D2 frames 135 and 136: the pale box with its one sentence, and « Fermer » alone.
          <div
            className="mt-3.5 grid min-h-[260px] place-items-center rounded-xl bg-[color:var(--hk-line-soft)] p-4"
            aria-label={t("bank.visionneuse.apercu")}
            role="group"
            data-testid="bank-visionneuse-apercu"
          >
            {phase.kind === "loading" ? (
              <p
                className="text-sm text-[color:var(--hk-text-2)]"
                data-testid="bank-visionneuse-ouverture"
                role="status"
              >
                {t("bank.visionneuse.ouverture")}
              </p>
            ) : (
              <p
                className="inline-flex items-center gap-2 text-sm text-[color:var(--hk-warn)]"
                data-testid="bank-visionneuse-indisponible"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t("bank.visionneuse.indisponible")}
              </p>
            )}
          </div>
        ) : (
          <div className="mt-3.5 grid grid-cols-[1.1fr_1fr] gap-5 max-[1099px]:grid-cols-1">
            <div
              className="grid gap-3 rounded-xl bg-[color:var(--hk-line-soft)] p-4"
              aria-label={t("bank.visionneuse.apercu")}
              role="group"
              data-testid="bank-visionneuse-apercu"
            >
              {files.map((file, i) => (
                <DocumentFile
                  key={`${String(i)}-${file.fileName}`}
                  file={file}
                  index={i}
                  total={files.length}
                />
              ))}
              {props.reason === null ? null : (
                <div
                  className="rounded-lg border-2 border-[color:var(--hk-brand)] bg-[color:var(--hk-brand-wash)] px-2.5 py-2"
                  data-testid="bank-visionneuse-extrait"
                >
                  <span className="block text-sm font-semibold uppercase tracking-[.08em] text-[color:var(--hk-brand-strong)]">
                    {t("bank.visionneuse.extrait")}
                  </span>
                  <b className="text-sm font-semibold text-[color:var(--hk-text)]">{props.reason}</b>
                </div>
              )}
            </div>
            <div>
              {props.checks.length === 0 ? null : (
                <div data-testid="bank-visionneuse-verifications">
                  <h3 className="mb-2 text-base font-semibold text-[color:var(--hk-text)]">
                    {t("bank.visionneuse.verifications")}
                  </h3>
                  <ul className="m-0 grid list-none gap-2 p-0">
                    {props.checks.map((one, index) => (
                      <li key={index} className="text-sm text-[color:var(--hk-text)]">
                        <Verdict kind={one.kind} label={one.label} /> {one.reason}.
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {props.verdict === null || props.reason === null ? null : (
                <p
                  className={`text-sm text-[color:var(--hk-text)]${props.checks.length === 0 ? "" : " mt-3"}`}
                  data-testid="bank-visionneuse-conclusion"
                >
                  <b>{t("bank.visionneuse.conclusion")}</b>{" "}
                  <Verdict kind={props.verdict.kind} label={props.verdict.label} /> {props.reason}
                </p>
              )}
            </div>
          </div>
        )}
        <div className="mt-[18px] flex flex-wrap items-center justify-end gap-2">
          <button
            ref={close}
            type="button"
            onClick={props.onClose}
            className={
              props.fromConversation === true
                ? "inline-flex items-center gap-2 rounded-[10px] bg-[color:var(--hk-text)] px-4 py-2 text-sm font-semibold text-white"
                : "rounded-[10px] px-4 py-2 text-sm font-semibold text-[color:var(--hk-brand-strong)] hover:bg-[color:var(--hk-brand-wash)]"
            }
            data-testid="bank-visionneuse-fermer"
          >
            {props.fromConversation === true ? (
              <>
                <Minimize2 width={16} height={16} aria-hidden="true" />
                {t("bank.conversation.retour")}
              </>
            ) : (
              t("bank.visionneuse.fermer")
            )}
          </button>
          {phase.kind === "loading" || files.length === 0 ? null : props.acts === null ? (
            <span
              className="text-sm text-[color:var(--hk-text-2)]"
              data-testid="bank-visionneuse-consultation"
            >
              {props.fromConversation === true
                ? t("bank.conversation.consultationSeule")
                : t("bank.visionneuse.consultation")}
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={props.acts.onFlag}
                className="rounded-[10px] border border-[color:var(--hk-line)] bg-white px-4 py-2 text-sm font-semibold text-[color:var(--hk-text)]"
                data-testid="bank-visionneuse-signaler"
              >
                {t("bank.revue.signaler")}
              </button>
              <button
                type="button"
                onClick={props.acts.onConform}
                className="rounded-[10px] bg-[color:var(--hk-text)] px-4 py-2 text-sm font-semibold text-white"
                data-testid="bank-visionneuse-conforme"
              >
                {t("bank.visionneuse.marquerConforme")}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
